/**
 * Stock Report V2 Page — DataGrid POC
 *
 * Mirror of `StockReportPage` wired to `StockReportTableV2` (which uses
 * `@bill/ui/DataGrid`). Same filters, KPIs, URL deep-linking and data
 * hooks — only the table component differs. Lives behind a separate
 * route so it can be compared side-by-side with the DevExpress version.
 */

import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, Package, AlertTriangle, TrendingUp } from 'lucide-react';
import { KPICard, Skeleton, EmptyState } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useCurrency, useCanSeeFinancials, useFormatDate, useCutoffHour } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { useStockReport, useStockReportKPIs } from '../hooks/useStockReport';
import { StockReportFilters } from '../components/StockReportFilters';
import { StockReportTableV2 } from '../components/StockReportTableV2';
import { StockReportTotalsSummary } from '../components/StockReportTotals';
import type { StockReportQueryParams, MarketType } from '@bstock/shared';

function dateRangeToParams(range: DateRange): { dateFrom: string; dateTo: string } | null {
  if (!range.start || !range.end) return null;
  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const datePart = `${year}-${month}-${day}`;
    const hours = d.getHours();
    const minutes = d.getMinutes();
    if (hours === 0 && minutes === 0) return datePart;
    return `${datePart}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };
  return {
    dateFrom: formatDate(range.start),
    dateTo: formatDate(range.end),
  };
}

function parseUrlParams(
  searchParams: URLSearchParams,
  cutoffHour: number
): {
  dateRange: DateRange;
  categoryId: string;
  marketType: string;
  rawProductId: string;
  bulkFilter: string;
} {
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1, cutoffHour, 0, 0, 0);
  const defaultEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    cutoffHour,
    0,
    0,
    0
  );
  return {
    dateRange: {
      start: dateFrom ? new Date(dateFrom) : defaultStart,
      end: dateTo ? new Date(dateTo) : defaultEnd,
    },
    categoryId: searchParams.get('categoryId') ?? '',
    marketType: searchParams.get('marketType') ?? '',
    rawProductId: searchParams.get('rawProductId') ?? '',
    bulkFilter: searchParams.get('bulkFilter') ?? '',
  };
}

export default function StockReportV2Page() {
  const currency = useCurrency();
  const showFinancials = useCanSeeFinancials();
  const { formatDateTime } = useFormatDate();
  const cutoffHour = useCutoffHour();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlParams = useMemo(
    () => parseUrlParams(searchParams, cutoffHour),
    [searchParams, cutoffHour]
  );

  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [categoryId, setCategoryId] = useState(urlParams.categoryId);
  const [marketType, setMarketType] = useState(urlParams.marketType);
  const [rawProductId, setRawProductId] = useState(urlParams.rawProductId);
  const [bulkFilter, setBulkFilter] = useState(urlParams.bulkFilter);

  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) newParams.set(key, value);
        else newParams.delete(key);
      });
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const queryParams = useMemo<StockReportQueryParams | null>(() => {
    const dates = dateRangeToParams(dateRange);
    if (!dates) return null;
    return {
      ...dates,
      categoryId: categoryId || undefined,
      marketType: (marketType as MarketType) || undefined,
      rawProductId: rawProductId || undefined,
      bulkFilter: (bulkFilter as 'bulk' | 'non-bulk') || undefined,
    };
  }, [dateRange, categoryId, marketType, rawProductId, bulkFilter]);

  const { data: reportData, isLoading, error } = useStockReport(queryParams);
  const { data: kpis } = useStockReportKPIs(queryParams);

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      const dates = dateRangeToParams(range);
      if (dates) updateUrl({ dateFrom: dates.dateFrom, dateTo: dates.dateTo });
    },
    [updateUrl]
  );

  const handleCategoryChange = useCallback(
    (value: string) => {
      setCategoryId(value);
      updateUrl({ categoryId: value || null });
    },
    [updateUrl]
  );

  const handleMarketTypeChange = useCallback(
    (value: string) => {
      setMarketType(value);
      updateUrl({ marketType: value || null });
    },
    [updateUrl]
  );

  const handleRawProductChange = useCallback(
    (value: string) => {
      setRawProductId(value);
      updateUrl({ rawProductId: value || null });
    },
    [updateUrl]
  );

  const handleBulkFilterChange = useCallback(
    (value: string) => {
      setBulkFilter(value);
      updateUrl({ bulkFilter: value || null });
    },
    [updateUrl]
  );

  const hasValidDates = dateRange.start && dateRange.end;

  return (
    <div className="min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <BarChart3 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-dark-500">
              Rapport de Stock{' '}
              <span className="text-xs font-normal text-primary-600 uppercase tracking-wider align-middle">
                v2 — DataGrid POC
              </span>
            </h1>
            <p className="text-sm text-neutral-500 mt-0.5">
              Version expérimentale basée sur le DataGrid Tailwind-native
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      {hasValidDates && (
        <div className="bg-white border-b border-neutral-200 px-6 py-4">
          <div
            className={`grid grid-cols-1 ${showFinancials ? 'lg:grid-cols-2' : 'lg:grid-cols-2'} gap-4`}
          >
            {showFinancials && (
              <>
                <KPICard
                  title="Valeur Stock Final"
                  value={kpis ? formatCentimes(kpis.totalStockValueCentimes, currency) : '-'}
                  icon={<Package className="w-5 h-5" />}
                />
                <KPICard
                  title="Ecart Total"
                  value={kpis ? formatCentimes(kpis.totalVarianceCentimes, currency) : '-'}
                  icon={<TrendingUp className="w-5 h-5" />}
                  badge={
                    kpis && kpis.totalVarianceCentimes > 0
                      ? { label: 'Perte', variant: 'danger' }
                      : kpis && kpis.totalVarianceCentimes < 0
                        ? { label: 'Gain', variant: 'success' }
                        : undefined
                  }
                />
              </>
            )}
            <KPICard
              title="Produits avec ecart"
              value={kpis?.productsWithVarianceCount ?? 0}
              icon={<AlertTriangle className="w-5 h-5" />}
              badge={
                kpis && kpis.productsWithVarianceCount > 0
                  ? { label: 'A verifier', variant: 'warning' }
                  : undefined
              }
            />
            <KPICard
              title="Ruptures de stock"
              value={kpis?.outOfStockCount ?? 0}
              icon={<Package className="w-5 h-5" />}
              badge={
                kpis && kpis.outOfStockCount > 0
                  ? { label: 'Critique', variant: 'danger' }
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <StockReportFilters
          dateRange={dateRange}
          categoryId={categoryId}
          marketType={marketType}
          rawProductId={rawProductId}
          bulkFilter={bulkFilter}
          onDateRangeChange={handleDateRangeChange}
          onCategoryChange={handleCategoryChange}
          onMarketTypeChange={handleMarketTypeChange}
          onRawProductChange={handleRawProductChange}
          onBulkFilterChange={handleBulkFilterChange}
        />
      </div>

      {/* Content */}
      <div className="p-6 bg-neutral-50">
        {!hasValidDates ? (
          <EmptyState
            icon={BarChart3}
            title="Selectionnez une periode"
            description="Choisissez une date de debut et de fin pour generer le rapport"
          />
        ) : isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-96" />
          </div>
        ) : error ? (
          <EmptyState
            icon={AlertTriangle}
            title="Erreur de chargement"
            description="Une erreur est survenue lors du chargement du rapport"
          />
        ) : reportData ? (
          <div className="space-y-6">
            {showFinancials && <StockReportTotalsSummary totals={reportData.totals} />}

            <StockReportTableV2
              rows={reportData.rows}
              isLoading={false}
              showFinancials={showFinancials}
              dateFrom={queryParams?.dateFrom}
              dateTo={queryParams?.dateTo}
            />

            <div className="text-xs text-neutral-400 text-right pb-4">
              {reportData.meta.productCount} produits | {reportData.meta.categoryCount} categories |
              Genere le {formatDateTime(reportData.meta.generatedAt)}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={Package}
            title="Aucune donnee"
            description="Aucune donnee disponible pour cette periode et ces filtres"
          />
        )}
      </div>
    </div>
  );
}
